const STORAGE_PREFIX = "mipl:list:";

/** Keys commonly used for list filters / pagination in the URL. */
export const LIST_QUERY_KEYS = [
  "page",
  "limit",
  "search",
  "status",
  "tab",
  "role",
  "mipl_location_id",
  "from_date",
  "to_date",
  "month",
  "balance",
  "fuel_pump_id",
  "company_id",
  "route_id",
];

const pickListParams = (obj) => {
  const out = {};
  if (!obj || typeof obj !== "object") return out;
  for (const key of LIST_QUERY_KEYS) {
    const v = obj[key];
    if (v != null && String(v).trim() !== "") out[key] = String(v);
  }
  return out;
};

export const saveListState = (listPath, state) => {
  try {
    sessionStorage.setItem(STORAGE_PREFIX + listPath, JSON.stringify(pickListParams(state)));
  } catch {
    /* ignore */
  }
};

export const loadListState = (listPath) => {
  try {
    const raw = sessionStorage.getItem(STORAGE_PREFIX + listPath);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
};

export const listStateFromSearchParams = (searchParams) => {
  const out = {};
  if (!searchParams) return out;
  for (const key of LIST_QUERY_KEYS) {
    const v = searchParams.get(key);
    if (v != null && v !== "") out[key] = v;
  }
  return out;
};

export const listStateToSearchParams = (state) => {
  const params = new URLSearchParams();
  const picked = pickListParams(state);
  Object.entries(picked).forEach(([k, v]) => params.set(k, v));
  return params;
};

export const mergeListSearchParams = (current, patch) => {
  const next = new URLSearchParams(current?.toString?.() ?? "");
  Object.entries(pickListParams(patch)).forEach(([k, v]) => {
    next.set(k, v);
  });
  Object.keys(patch || {}).forEach((k) => {
    if (LIST_QUERY_KEYS.includes(k) && (patch[k] == null || patch[k] === "")) {
      next.delete(k);
    }
  });
  return next;
};

export const encodeReturnTo = (pathname, search = "") => {
  const qs = typeof search === "string" ? search : search?.toString?.() ?? "";
  const normalized = qs.startsWith("?") ? qs : qs ? `?${qs}` : "";
  return encodeURIComponent(`${pathname}${normalized}`);
};

export const decodeReturnTo = (value, fallback) => {
  if (!value) return fallback;
  try {
    let decoded = decodeURIComponent(value);
    if (
      !decoded.startsWith("/") &&
      (decoded.includes("%2F") || decoded.includes("%2f") || decoded.includes("%3F") || decoded.includes("%3f"))
    ) {
      decoded = decodeURIComponent(decoded);
    }
    if (decoded.startsWith("/") && !decoded.startsWith("//")) return decoded;
  } catch {
    /* ignore */
  }
  return fallback;
};

const decodeReturnToParam = (value) => {
  if (!value) return null;
  try {
    let decoded = decodeURIComponent(value);
    if (
      !decoded.startsWith("/") &&
      (decoded.includes("%2F") || decoded.includes("%2f") || decoded.includes("%3F") || decoded.includes("%3f"))
    ) {
      decoded = decodeURIComponent(decoded);
    }
    if (!decoded.startsWith("/") || decoded.startsWith("//")) return null;
    return decoded;
  } catch {
    return null;
  }
};

/** Update list query keys inside a `returnTo` param (e.g. sync mipl location on back navigation). */
export const patchReturnToListQuery = (returnToParam, patch) => {
  const decoded = decodeReturnToParam(returnToParam);
  if (!decoded) return returnToParam;

  const qIndex = decoded.indexOf("?");
  const path = qIndex === -1 ? decoded : decoded.slice(0, qIndex);
  const params = new URLSearchParams(qIndex === -1 ? "" : decoded.slice(qIndex + 1));
  const picked = pickListParams(patch);

  Object.entries(picked).forEach(([key, val]) => {
    params.set(key, val);
  });
  Object.keys(patch || {}).forEach((key) => {
    if (LIST_QUERY_KEYS.includes(key) && (patch[key] == null || patch[key] === "")) {
      params.delete(key);
    }
  });

  const qs = params.toString();
  return encodeURIComponent(qs ? `${path}?${qs}` : path);
};

export const buildReturnToParam = (pathname, searchParams) =>
  encodeReturnTo(pathname, searchParams?.toString?.() ? `?${searchParams.toString()}` : "");

export const appendReturnToQuery = (href, returnToParam) => {
  if (!returnToParam) return href;
  const sep = href.includes("?") ? "&" : "?";
  return `${href}${sep}returnTo=${returnToParam}`;
};

export const parsePositiveInt = (value, fallback) => {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : fallback;
};
